import { supabase } from '../clients/supabaseClient';
import type { CollectionPoint } from '../types';

// ── Types ──────────────────────────────────────────────────────────────────────

// Payload required when creating a new collection point.
// `is_active` is required on creation so the point starts in a known state.
export type CreateCollectionPointPayload = {
    name: string;
    address_line1: string;
    suburb?: string | null;
    city?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    instructions?: string | null;
    is_active: boolean;
};

// All fields are optional on update except `is_active`, which can also be patched
// directly (e.g. via the toggleActive action in the hook).
export type UpdateCollectionPointPayload = Partial<Omit<CreateCollectionPointPayload, 'is_active'>> & {
    is_active?: boolean;
};

// ── Service functions ──────────────────────────────────────────────────────────

// Fetches all collection points ordered by creation date ascending,
// so the list order is stable and reflects insertion order.
export async function fetchCollectionPointsFromDb(): Promise<CollectionPoint[]> {
    const { data, error } = await supabase
        .from('collection_points')
        .select('*')
        .order('created_at', { ascending: true });

    if (error) throw error;
    return (data ?? []) as CollectionPoint[];
}

// Inserts a new collection point and returns the created row.
// `.single()` ensures we get a typed object back rather than an array.
export async function createCollectionPointInDb(
    payload: CreateCollectionPointPayload
): Promise<CollectionPoint> {
    const { data, error } = await supabase
        .from('collection_points')
        .insert([payload])
        .select()
        .single();

    if (error) throw error;
    return data as CollectionPoint;
}

// Updates an existing collection point by ID and returns the updated row.
// `updated_at` is stamped client-side here because the table does not have a
// trigger to handle it automatically.
export async function updateCollectionPointInDb(
    id: string,
    patch: UpdateCollectionPointPayload
): Promise<CollectionPoint> {
    const { data, error } = await supabase
        .from('collection_points')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

    if (error) throw error;
    return data as CollectionPoint;
}

// Hard-deletes a collection point by ID.
// If soft-delete behaviour is ever needed, replace this with an
// `update({ deleted_at: new Date().toISOString() })` call instead.
export async function deleteCollectionPointFromDb(id: string): Promise<void> {
    const { error } = await supabase
        .from('collection_points')
        .delete()
        .eq('id', id);

    if (error) throw error;
}